import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import formService from '../../../services/formService';
import { APPLICATION_STATUS, getStatusColor, formatDateTime, formatCurrency } from '../helpers';

/**
 * Application Details Modal Component
 * Displays comprehensive application information including form responses,
 * interview details, and hiring offer information
 */
const ApplicationDetailsModal = ({ application, onClose }) => {
  const [formResponseData, setFormResponseData] = useState(null);
  const [loadingFormResponses, setLoadingFormResponses] = useState(false);
  const [formResponseError, setFormResponseError] = useState(null);

  // Load form responses when modal opens
  useEffect(() => {
    if (!application?.id) return;

    const loadFormResponses = async () => {
      try {
        setLoadingFormResponses(true);
        setFormResponseError(null);
        const result = await formService.getApplicationFormResponses(application.id);
        
        if (result?.success && result.data?.length > 0) {
          setFormResponseData(result.data[0]);
        }
      } catch (error) {
        console.error('Error loading form responses:', error);
        setFormResponseError('Failed to load application form responses');
      } finally {
        setLoadingFormResponses(false);
      }
    };

    loadFormResponses();
  }, [application?.id]);

  if (!application) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-border-primary">
          <div>
            <h2 className="text-2xl font-bold text-text-primary">
              {application.job?.title || 'Application Details'}
            </h2>
            <p className="text-text-secondary">
              {application.job?.company_name || 'Unknown Company'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-background-secondary rounded-full transition-colors"
          >
            <Icon name="X" size={24} className="text-text-muted" />
          </button>
        </div>

        {/* Modal Content */}
        <div className="p-6 space-y-6">
          {/* Application Overview */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-text-primary">Application Details</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-text-secondary">Applied Date:</span>
                  <span className="text-text-primary">{formatDateTime(application.applied_at)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-text-secondary">Proposed Rate:</span>
                  <span className="text-text-primary">{formatCurrency(application.proposed_rate)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-text-secondary">Status:</span>
                  <span className={`px-2 py-1 rounded-full text-sm font-medium capitalize ${getStatusColor(application.status)}`}>
                    {application.status}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-text-secondary">Match Percentage:</span>
                  <span className="text-text-primary">{application.match_percentage || 0}%</span>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-text-primary">Job Information</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-text-secondary">Location:</span>
                  <span className="text-text-primary">
                    {application.job?.is_remote ? 'Remote' : application.job?.location || 'Location TBD'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-text-secondary">Job Type:</span>
                  <span className="text-text-primary capitalize">
                    {application.job?.job_type?.replace('_', ' ') || 'Not specified'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-text-secondary">Experience Level:</span>
                  <span className="text-text-primary capitalize">
                    {application.job?.experience_level || 'Not specified'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Cover Letter */}
          {application.cover_letter && (
            <div className="space-y-3">
              <h3 className="text-lg font-semibold text-text-primary">Cover Letter</h3>
              <div className="p-4 bg-background-secondary rounded-lg">
                <p className="text-text-secondary whitespace-pre-wrap">{application.cover_letter}</p>
              </div>
            </div>
          )}

          {/* Form Responses */}
          {loadingFormResponses && (
            <div className="flex items-center justify-center py-6">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
              <span className="ml-3 text-text-secondary">Loading form responses...</span>
            </div>
          )}

          {formResponseError && (
            <div className="bg-error-50 border border-error-200 rounded-lg p-4">
              <p className="text-error text-sm">{formResponseError}</p>
            </div>
          )}

          {!loadingFormResponses && !formResponseError && formResponseData && (
            <div className="bg-blue-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-text-primary mb-2 flex items-center">
                <Icon name="FileText" size={20} className="mr-2 text-blue-600" />
                Application Form Responses
              </h3>
              
              {/* Form Title and Description */}
              {formResponseData.employer_forms && (
                <div className="mb-4 pb-4 border-b border-blue-200">
                  <h4 className="text-md font-semibold text-text-primary mb-1">
                    {formResponseData.employer_forms.title}
                  </h4>
                  {formResponseData.employer_forms.description && (
                    <p className="text-sm text-text-secondary">
                      {formResponseData.employer_forms.description}
                    </p>
                  )}
                </div>
              )}

              <div className="space-y-4">
                {Object.entries(formResponseData.responses || {}).map(([questionId, response]) => (
                  <div key={questionId} className="bg-white p-4 rounded-lg border border-blue-100">
                    <div className="font-medium text-sm text-text-primary mb-2">
                      {response.question}
                    </div>
                    <div className="text-sm text-text-secondary">
                      {Array.isArray(response.answer) ? (
                        <ul className="list-disc list-inside space-y-1">
                          {response.answer.map((item, idx) => (
                            <li key={idx}>{item}</li>
                          ))}
                        </ul>
                      ) : response.type === 'url' ? (
                        <a 
                          href={response.answer} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-primary hover:text-primary-dark underline flex items-center"
                        >
                          {response.answer}
                          <Icon name="ExternalLink" size={14} className="ml-1" />
                        </a>
                      ) : (
                        <p className="whitespace-pre-wrap">{response.answer}</p>
                      )}
                    </div>
                  </div>
                ))}
                <div className="text-xs text-text-muted italic mt-4">
                  Submitted: {new Date(formResponseData.submitted_at).toLocaleString()}
                </div>
              </div>
            </div>
          )}

          {/* Interview Details */}
          {(application.status === APPLICATION_STATUS.INTERVIEWED || application.interview_data?.feedback || application.interview_data?.hiring_notes) && (
            <InterviewDetails application={application} />
          )}

          {/* Hiring Offer Details */}
          {application.status === APPLICATION_STATUS.HIRED && (
            <HiringOfferDetails application={application} />
          )}

          {/* Employer Notes */}
          {application.employer_notes && (
            <div className="space-y-3">
              <h3 className="text-lg font-semibold text-text-primary">Employer Notes</h3>
              <div className="p-4 bg-primary-50 border border-primary-200 rounded-lg">
                <p className="text-primary">{application.employer_notes}</p>
              </div>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="flex justify-end space-x-3 p-6 border-t border-border-primary">
          <Button onClick={onClose} variant="outline">
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

/**
 * Interview Details Section
 */
const InterviewDetails = React.memo(({ application }) => (
  <div className="space-y-4">
    <h3 className="text-lg font-semibold text-text-primary">Interview Information</h3>
    <div className="p-4 bg-accent-50 border border-accent-200 rounded-lg space-y-3">
      {application.interview_scheduled_at && (
        <div className="flex items-center space-x-2">
          <Icon name="Calendar" size={16} className="text-accent" />
          <span className="font-medium text-accent">
            Scheduled: {formatDateTime(application.interview_scheduled_at)}
          </span>
        </div>
      )}
      
      {application.interview_data?.scheduled_at && (
        <div className="flex items-center space-x-2">
          <Icon name="Calendar" size={16} className="text-accent" />
          <span className="font-medium text-accent">
            Interview Date: {formatDateTime(application.interview_data.scheduled_at)}
          </span>
        </div>
      )}
      
      {application.interview_data?.hiring_notes && (
        <div className="space-y-3 mt-4">
          <h4 className="font-medium text-text-primary">Interview Notes</h4>
          <p className="text-text-secondary">{application.interview_data.hiring_notes}</p>
        </div>
      )}

      {application.interview_data?.feedback && typeof application.interview_data.feedback === 'object' && (
        <InterviewFeedback feedback={application.interview_data.feedback} />
      )}
    </div>
  </div>
));

/**
 * Interview Feedback Section
 */
const InterviewFeedback = React.memo(({ feedback }) => {
  const renderFeedbackItems = (items) => {
    if (Array.isArray(items)) {
      return items.map((item, index) => (
        <span key={index} className="px-2 py-1 bg-success-100 text-success rounded-full text-sm">
          {item}
        </span>
      ));
    }
    if (typeof items === 'string') {
      return items.split(',').map((item, index) => (
        <span key={index} className="px-2 py-1 bg-success-100 text-success rounded-full text-sm">
          {item.trim()}
        </span>
      ));
    }
    return null;
  };

  return (
    <div className="space-y-3 mt-4">
      <h4 className="font-medium text-text-primary">Interview Feedback</h4>
      
      {feedback.strengths && (
        <div className="space-y-2">
          <h5 className="font-medium text-success">Strengths</h5>
          <div className="flex flex-wrap gap-2">
            {renderFeedbackItems(feedback.strengths)}
          </div>
        </div>
      )}

      {feedback.areas_for_improvement && (
        <div className="space-y-2">
          <h5 className="font-medium text-warning">Areas for Improvement</h5>
          <div className="flex flex-wrap gap-2">
            {Array.isArray(feedback.areas_for_improvement) 
              ? feedback.areas_for_improvement.map((area, index) => (
                  <span key={index} className="px-2 py-1 bg-warning-100 text-warning rounded-full text-sm">
                    {area}
                  </span>
                ))
              : typeof feedback.areas_for_improvement === 'string'
                ? feedback.areas_for_improvement.split(',').map((area, index) => (
                    <span key={index} className="px-2 py-1 bg-warning-100 text-warning rounded-full text-sm">
                      {area.trim()}
                    </span>
                  ))
                : null
            }
          </div>
        </div>
      )}

      {feedback.feedback && (
        <div className="space-y-2">
          <h5 className="font-medium text-text-primary">General Feedback</h5>
          <p className="text-text-secondary">{feedback.feedback}</p>
        </div>
      )}

      {feedback.recommendation && (
        <div className="space-y-2">
          <h5 className="font-medium text-text-primary">Recommendation</h5>
          <span className={`px-2 py-1 rounded-full text-sm font-medium capitalize ${
            feedback.recommendation === 'hire' ? 'bg-success-100 text-success' : 'bg-warning-100 text-warning'
          }`}>
            {feedback.recommendation}
          </span>
        </div>
      )}
    </div>
  );
});

/**
 * Hiring Offer Details Section
 */
const HiringOfferDetails = React.memo(({ application }) => (
  <div className="space-y-4">
    <h3 className="text-lg font-semibold text-text-primary">Hiring Offer</h3>
    <div className="p-4 bg-success-50 border border-success-200 rounded-lg space-y-3">
      {application.interview_data?.offer_amount && (
        <div className="flex justify-between">
          <span className="text-text-secondary">Offered Rate:</span>
          <span className="text-success font-medium">{formatCurrency(application.interview_data.offer_amount)}</span>
        </div>
      )}
      
      {application.interview_data?.decision_date && (
        <div className="flex justify-between">
          <span className="text-text-secondary">Decision Date:</span>
          <span className="text-success font-medium">{formatDateTime(application.interview_data.decision_date)}</span>
        </div>
      )}

      {application.interview_data?.hiring_notes && (
        <div className="space-y-2">
          <h4 className="font-medium text-success">Hiring Notes</h4>
          <p className="text-text-secondary">{application.interview_data.hiring_notes}</p>
        </div>
      )}

      {application.interview_data?.hiring_status && application.interview_data.hiring_status !== 'hired' && (
        <div className="space-y-2">
          <h4 className="font-medium text-success">Hiring Status</h4>
          <span className="px-2 py-1 bg-success-100 text-success rounded-full text-sm capitalize">
            {application.interview_data.hiring_status.replace('_', ' ')}
          </span>
        </div>
      )}
    </div>
  </div>
));

export default ApplicationDetailsModal;
